import { ComponentChild, VNode } from 'preact';
import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks';
import cn from 'classnames';

import { Typography } from '../Typography/Typography';
import { useSliderDragging } from '../../hooks/useSliderDragging';
import { useTranslation } from '../../../../utils/useTranslation';
import ns from '../../translations';
import { useComputedValue } from '../../hooks/useComputedValue';
import { CloseButton } from '../Overlay/CloseButton';

import { Option, OptionProps } from './Option';

interface SelectProps<T> {
  value: T;
  options: {
    label: ComponentChild;
    value: T;
  }[];
  onChange: (value: T) => void;
  size?: 'large' | 'small';
  categoryTitle?: string;
}

export const SeasonPicker = <T extends string | number>({
  options,
  value,
  onChange,
  categoryTitle,
  size = 'large',
}: SelectProps<T>): VNode | null => {
  const ref = useRef<HTMLDivElement>(null!);
  const sliderRef = useRef<HTMLUListElement>(null!);

  const { handleClickCapture } = useSliderDragging({ sliderRef });

  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [value, options]);

  const containerPadding = useComputedValue({
    element: ref.current,
    defaultValue: 0,
    property: 'paddingRight',
  });
  const sliderPaddingRight = useComputedValue({
    element: sliderRef.current,
    defaultValue: 0,
    property: 'paddingRight',
  });
  const sliderPaddingLeft = useComputedValue({
    element: sliderRef.current,
    defaultValue: 0,
    property: 'paddingLeft',
  });

  const scrollOffset =
    sliderPaddingLeft + containerPadding + sliderPaddingRight;

  const handleOptionClick: OptionProps<T>['onOptionClick'] = useCallback(
    ({ value: newValue, node }) => {
      onChange(newValue);

      const scrollDiff = sliderRef.current.scrollLeft - node.current.offsetLeft;

      sliderRef.current.scrollBy({
        left: -1 * (scrollDiff + scrollOffset),
        behavior: 'smooth',
      });
    },
    [onChange, scrollOffset]
  );

  useEffect(() => {
    sliderRef.current.scrollTo({ left: sliderRef.current.scrollWidth });
  }, []);

  return (
    <div className="vgp-picker" ref={ref}>
      <Typography
        variant="label"
        size="large"
        color="primary"
        className="vgp-picker-label"
      >
        {categoryTitle}
      </Typography>
      <ul
        className="vgp-picker-slider"
        ref={sliderRef}
        onClickCapture={handleClickCapture}
      >
        {options.map((option) => (
          <Option
            value={option.value}
            isSelected={option.value === selectedOption?.value}
            onOptionClick={handleOptionClick}
            size={size}
          >
            {option.label}
          </Option>
        ))}
      </ul>
      {size === 'small' && <CloseButton className="vgp-picker-close-button" />}
    </div>
  );
};
